// ไฟล์: frontend/src/pages/admin/TeacherTrainings.js
import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import Chart from 'chart.js/auto';

function AdminTeacherTrainings() {
  const [trainings, setTrainings] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    teacher_id: '',
    course_id: '',
    start_date: '',
    end_date: '',
    certificate_no: '',
    status: 'registered',
    note: '',
    certificate_file: null
  });
  const [formMode, setFormMode] = useState('add'); // 'add' หรือ 'edit'
  const [selectedTeacherId, setSelectedTeacherId] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [summaryChart, setSummaryChart] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  
  // ปีที่สามารถเลือกได้ (ปีปัจจุบัน - 5 ปี)
  const availableYears = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 6; i++) {
    availableYears.push((currentYear - i).toString());
  }
  
  useEffect(() => {
    fetchTeachers();
    fetchCourses();
    fetchTrainings();
  }, []);
  
  useEffect(() => {
    // กรองข้อมูลการอบรมตามครูและปี
    filterTrainings();
  }, [trainings, selectedTeacherId, selectedYear]);
  
  useEffect(() => {
    // สร้างแผนภูมิสรุปการอบรม
    if (filteredTrainings.length > 0) {
      createSummaryChart();
    }
    
    // Cleanup function
    return () => {
      if (summaryChart) {
        summaryChart.destroy();
        setSummaryChart(null);
      }
    };
  }, [filteredTrainings]);
  
  const fetchTeachers = async () => {
    try {
      const response = await api.user.getAllTeachers();
      setTeachers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };
  
  const fetchCourses = async () => {
    try {
      const response = await api.trainingCourse.getAllCourses();
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };
  
  const fetchTrainings = async () => {
    try {
      setIsLoading(true);
      const response = await api.teacherTraining.getAllTrainings();
      setTrainings(response.data.data || []);
    } catch (error) {
      console.error('Error fetching trainings:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลการอบรมได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const filterTrainings = () => {
    let filtered = [...trainings];
    
    // กรองตามครู
    if (selectedTeacherId !== 'all') {
      filtered = filtered.filter(training => training.teacher_id == selectedTeacherId);
    }
    
    // กรองตามปี
    filtered = filtered.filter(training => {
      const startDate = new Date(training.start_date);
      return startDate.getFullYear().toString() === selectedYear;
    });
    
    setFilteredTrainings(filtered);
  };
  
  const createSummaryChart = () => {
    // หาจำนวนการอบรมแยกตามสถานะ
    const completedCount = filteredTrainings.filter(t => t.status === 'completed').length;
    const inProgressCount = filteredTrainings.filter(t => t.status === 'in_progress').length;
    const registeredCount = filteredTrainings.filter(t => t.status === 'registered').length;
    const cancelledCount = filteredTrainings.filter(t => t.status === 'cancelled').length;
    
    // หาจำนวนการอบรมแยกตามประเภท
    const workshopCount = filteredTrainings.filter(t => t.course_type === 'workshop').length;
    const onlineCount = filteredTrainings.filter(t => t.course_type === 'online').length;
    const onsiteCount = filteredTrainings.filter(t => t.course_type === 'onsite').length;
    const seminarCount = filteredTrainings.filter(t => t.course_type === 'seminar').length;
    const otherCount = filteredTrainings.filter(t => t.course_type === 'other').length;
    
    // สร้างแผนภูมิ
    const ctx = document.getElementById('summaryChart');
    
    // ทำลายแผนภูมิเก่า (ถ้ามี)
    if (summaryChart) {
      summaryChart.destroy();
    }
    
    // สร้างแผนภูมิใหม่
    const newChart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: ['เสร็จสิ้น', 'กำลังอบรม', 'ลงทะเบียน', 'ยกเลิก'],
        datasets: [{
          data: [completedCount, inProgressCount, registeredCount, cancelledCount],
          backgroundColor: [
            '#28a745', // เขียว - เสร็จสิ้น
            '#17a2b8', // ฟ้า - กำลังอบรม
            '#ffc107', // เหลือง - ลงทะเบียน
            '#dc3545'  // แดง - ยกเลิก
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'สรุปการอบรมตามสถานะ'
          }
        }
      }
    });
    
    setSummaryChart(newChart);
  };
  
  const handleShowAddModal = () => {
    setFormData({
      id: '',
      teacher_id: teachers.length > 0 ? teachers[0].id : '',
      course_id: courses.length > 0 ? courses[0].id : '',
      start_date: '',
      end_date: '',
      certificate_no: '',
      status: 'registered',
      note: '',
      certificate_file: null
    });
    setPreviewFile(null);
    setFormMode('add');
    setShowModal(true);
  };
  
  const handleShowEditModal = (training) => {
    setFormData({
      id: training.id,
      teacher_id: training.teacher_id,
      course_id: training.course_id,
      start_date: training.start_date.substring(0, 10), // ตัดเวลาออก
      end_date: training.end_date.substring(0, 10), // ตัดเวลาออก
      certificate_no: training.certificate_no || '',
      status: training.status,
      note: training.note || '',
      certificate_file: null
    });
    
    // ตั้งค่าไฟล์ใบรับรอง (ถ้ามี)
    if (training.certificate_file) {
      setPreviewFile(`https://cms2568.backend.kruwirat.com/uploads/${training.certificate_file}`);
    } else {
      setPreviewFile(null);
    }
    
    setFormMode('edit');
    setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        certificate_file: file
      });
      
      // สร้าง URL สำหรับแสดงตัวอย่างไฟล์
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewFile(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบข้อมูลที่จำเป็น
    if (!formData.teacher_id || !formData.course_id || !formData.start_date || !formData.end_date) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
        text: 'กรุณาเลือกครู, หลักสูตร, วันที่เริ่มต้น และวันที่สิ้นสุด',
        confirmButtonColor: '#ff6b01'
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // สร้าง FormData สำหรับส่งข้อมูลพร้อมไฟล์
      const data = new FormData();
      data.append('teacher_id', formData.teacher_id);
      data.append('course_id', formData.course_id);
      data.append('start_date', formData.start_date);
      data.append('end_date', formData.end_date);
      data.append('certificate_no', formData.certificate_no);
      data.append('status', formData.status);
      data.append('note', formData.note);
      
      if (formData.certificate_file) {
        data.append('certificate_file', formData.certificate_file);
      }
      
      let response;
      if (formMode === 'add') {
        // เพิ่มการอบรมใหม่
        response = await api.teacherTraining.createTraining(data);
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'บันทึกข้อมูลการอบรมเรียบร้อยแล้ว',
          confirmButtonColor: '#ff6b01'
        });
      } else {
        // แก้ไขข้อมูลการอบรม
        response = await api.teacherTraining.updateTraining(formData.id, data);
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'อัปเดตข้อมูลการอบรมเรียบร้อยแล้ว',
          confirmButtonColor: '#ff6b01'
        });
      }
      
      // ปิด Modal และโหลดข้อมูลใหม่
      setShowModal(false);
      fetchTrainings();
    } catch (error) {
      console.error('Error saving training:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกข้อมูลการอบรมได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDelete = (id, courseName) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: `คุณแน่ใจหรือไม่ว่าต้องการลบข้อมูลการอบรม "${courseName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6b01',
      cancelButtonColor: '#d33',
      confirmButtonText: 'ใช่, ลบข้อมูล',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.teacherTraining.deleteTraining(id);
          Swal.fire({
            icon: 'success',
            title: 'ลบข้อมูลสำเร็จ',
            text: 'ลบข้อมูลการอบรมเรียบร้อยแล้ว',
            confirmButtonColor: '#ff6b01'
          });
          fetchTrainings();
        } catch (error) {
          console.error('Error deleting training:', error);
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบข้อมูลการอบรมได้',
            confirmButtonColor: '#ff6b01'
          });
        }
      }
    });
  };
  
  // แปลงสถานะเป็นภาษาไทยและสี
  const getStatusBadge = (status) => {
    switch(status) {
      case 'completed':
        return <span className="badge badge-success">เสร็จสิ้น</span>;
      case 'in_progress':
        return <span className="badge badge-info">กำลังอบรม</span>;
      case 'registered':
        return <span className="badge badge-warning">ลงทะเบียน</span>;
      case 'cancelled':
        return <span className="badge badge-danger">ยกเลิก</span>;
      default:
        return <span className="badge badge-secondary">{status}</span>;
    }
  };
  
  // หาชื่อหลักสูตรจาก ID
  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id == courseId);
    return course ? course.course_name : '';
  };
  
  // คำนวณจำนวนชั่วโมงทั้งหมดในปีนี้
  const calculateTotalHours = () => {
    if (filteredTrainings.length === 0) return 0;
    
    return filteredTrainings
      .filter(t => t.status === 'completed')
      .reduce((total, training) => total + parseFloat(training.hours), 0);
  };
  
  // คำนวณจำนวนหลักสูตรที่เสร็จสิ้นแล้ว
  const getCompletedCount = () => {
    return filteredTrainings.filter(t => t.status === 'completed').length;
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">รายงานการอบรมของครู</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/admin/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">รายงานการอบรม</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {/* ตัวกรองข้อมูล */}
            <div className="card card-orange">
              <div className="card-header">
                <h3 className="card-title">ตัวกรองข้อมูล</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>ครูผู้สอน</label>
                      <select
                        className="form-control"
                        value={selectedTeacherId}
                        onChange={(e) => setSelectedTeacherId(e.target.value)}
                      >
                        <option value="all">ทั้งหมด</option>
                        {teachers.map(teacher => (
                          <option key={teacher.id} value={teacher.id}>
                            {teacher.first_name} {teacher.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>ปี</label>
                      <select
                        className="form-control"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                      >
                        {availableYears.map(year => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <button className="btn btn-orange" onClick={handleShowAddModal}>
                      <i className="fas fa-plus mr-2"></i>
                      เพิ่มข้อมูลการอบรม
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* สรุปข้อมูลการอบรม */}
            <div className="row">
              <div className="col-md-3 col-sm-6 col-12">
                <div className="info-box bg-info">
                  <span className="info-box-icon">
                    <i className="fas fa-certificate"></i>
                  </span>
                  <div className="info-box-content">
                    <span className="info-box-text">หลักสูตรทั้งหมด</span>
                    <span className="info-box-number">{filteredTrainings.length}</span>
                    <div className="progress">
                      <div className="progress-bar" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6 col-12">
                <div className="info-box bg-success">
                  <span className="info-box-icon">
                    <i className="fas fa-check-circle"></i>
                  </span>
                  <div className="info-box-content">
                    <span className="info-box-text">เสร็จสิ้นแล้ว</span>
                    <span className="info-box-number">{getCompletedCount()}</span>
                    <div className="progress">
                      <div 
                        className="progress-bar" 
                        style={{ 
                          width: filteredTrainings.length > 0 ? `${getCompletedCount() / filteredTrainings.length * 100}%` : '0%' 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6 col-12">
                <div className="info-box bg-warning">
                  <span className="info-box-icon">
                    <i className="fas fa-clock"></i>
                  </span>
                  <div className="info-box-content">
                    <span className="info-box-text">จำนวนชั่วโมงรวม</span>
                    <span className="info-box-number">{calculateTotalHours()}</span>
                    <div className="progress">
                      <div className="progress-bar" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6 col-12">
                <div className="info-box bg-orange">
                  <span className="info-box-icon">
                    <i className="fas fa-users"></i>
                  </span>
                  <div className="info-box-content">
                    <span className="info-box-text">จำนวนครูที่อบรม</span>
                    <span className="info-box-number">
                      {
                        selectedTeacherId === 'all' 
                          ? new Set(filteredTrainings.map(t => t.teacher_id)).size 
                          : (filteredTrainings.length > 0 ? 1 : 0)
                      }
                    </span>
                    <div className="progress">
                      <div className="progress-bar" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="row">
              {/* แผนภูมิสรุป */}
              <div className="col-md-5">
                <div className="card card-orange">
                  <div className="card-header">
                    <h3 className="card-title">สรุปการอบรม</h3>
                  </div>
                  <div className="card-body">
                    <canvas id="summaryChart"></canvas>
                  </div>
                </div>
              </div>
              
              {/* รายการการอบรม */}
              <div className="col-md-7">
                <div className="card card-orange">
                  <div className="card-header">
                    <h3 className="card-title">รายการอบรมของครู</h3>
                  </div>
                  <div className="card-body">
                    {isLoading ? (
                      <div className="loading-spinner">
                        <i className="fas fa-spinner fa-spin"></i>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                          <thead>
                            <tr>
                              <th style={{ width: '25%' }}>ครูผู้สอน</th>
                              <th style={{ width: '25%' }}>หลักสูตร</th>
                              <th style={{ width: '20%' }}>วันที่</th>
                              <th style={{ width: '15%' }}>สถานะ</th>
                              <th style={{ width: '15%' }}>ตัวเลือก</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTrainings.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center">
                                  ไม่พบข้อมูลการอบรมที่ตรงตามเงื่อนไข
                                </td>
                              </tr>
                            ) : (
                              filteredTrainings.map((training) => (
                                <tr key={training.id}>
                                  <td>{training.teacher_name}</td>
                                  <td>
                                    {training.course_name}
                                    <small className="d-block">
                                      {training.organizer}
                                    </small>
                                  </td>
                                  <td>
                                    {new Date(training.start_date).toLocaleDateString('th-TH')}
                                    {' - '}
                                    {new Date(training.end_date).toLocaleDateString('th-TH')}
                                    <small className="d-block">
                                      {training.hours} ชั่วโมง
                                    </small>
                                  </td>
                                  <td>{getStatusBadge(training.status)}</td>
                                  <td>
                                    <button
                                      className="btn btn-info btn-sm mr-1"
                                      onClick={() => handleShowEditModal(training)}
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      className="btn btn-danger btn-sm"
                                      onClick={() => handleDelete(training.id, training.course_name)}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Modal เพิ่ม/แก้ไขข้อมูลการอบรม */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-orange">
                <h5 className="modal-title">
                  {formMode === 'add' ? 'เพิ่มข้อมูลการอบรมใหม่' : 'แก้ไขข้อมูลการอบรม'}
                </h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>ครูผู้สอน <span className="text-danger">*</span></label>
                        <select
                          className="form-control"
                          name="teacher_id"
                          value={formData.teacher_id}
                          onChange={handleInputChange}
                          required
                        >
                          {teachers.map(teacher => (
                            <option key={teacher.id} value={teacher.id}>
                              {teacher.first_name} {teacher.last_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>หลักสูตร <span className="text-danger">*</span></label>
                        <select
                          className="form-control"
                          name="course_id"
                          value={formData.course_id}
                          onChange={handleInputChange}
                          required
                        >
                          {courses.map(course => (
                            <option key={course.id} value={course.id}>
                              {course.course_name} ({course.hours} ชม.)
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>วันที่เริ่มต้น <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          name="start_date"
                          value={formData.start_date}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>วันที่สิ้นสุด <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          name="end_date"
                          value={formData.end_date}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>เลขที่วุฒิบัตร/เกียรติบัตร</label>
                        <input
                          type="text"
                          className="form-control"
                          name="certificate_no"
                          value={formData.certificate_no}
                          onChange={handleInputChange}
                          placeholder="เช่น CERT-2025-001"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>สถานะ <span className="text-danger">*</span></label>
                        <select
                          className="form-control"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="registered">ลงทะเบียน</option>
                          <option value="in_progress">กำลังอบรม</option>
                          <option value="completed">เสร็จสิ้น</option>
                          <option value="cancelled">ยกเลิก</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>ไฟล์วุฒิบัตร/เกียรติบัตร</label>
                    <div className="custom-file">
                      <input
                        type="file"
                        className="custom-file-input"
                        id="certificate_file"
                        name="certificate_file"
                        onChange={handleFileChange}
                        accept="image/*,.pdf"
                      />
                      <label className="custom-file-label" htmlFor="certificate_file">
                        {formData.certificate_file 
                          ? formData.certificate_file.name 
                          : (previewFile ? 'มีไฟล์อยู่แล้ว (คลิกเพื่อเปลี่ยน)' : 'เลือกไฟล์')
                        }
                      </label>
                    </div>
                    {previewFile && (
                      <div className="mt-2 text-center">
                        {previewFile.toLowerCase().endsWith('.pdf') ? (
                          <a href={previewFile} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-info">
                            <i className="fas fa-file-pdf mr-1"></i> เปิดไฟล์ PDF
                          </a>
                        ) : (
                          <img 
                            src={previewFile} 
                            alt="Certificate Preview" 
                            className="img-fluid" 
                            style={{ maxHeight: '200px' }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>หมายเหตุ</label>
                    <textarea
                      className="form-control"
                      name="note"
                      value={formData.note}
                      onChange={handleInputChange}
                      placeholder="ข้อมูลเพิ่มเติมเกี่ยวกับการอบรม"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-orange" disabled={isLoading}>
                    {isLoading ? (
                      <><i className="fas fa-spinner fa-spin"></i> กำลังบันทึก...</>
                    ) : (
                      <>บันทึก</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* พื้นหลังทึบ */}
          <div className="modal- fade show"></div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default AdminTeacherTrainings;